import React, { useState, useMemo } from 'react';

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  green800: '#1a4a1c',
  green700: '#1e5e20',
  green600: '#2a7a2d',
  green500: '#3a9a3e',
  green200: '#b8e0bb',
  green100: '#dff0e0',
  green50:  '#f0f9f1',
  bg:       '#f5f8f5',
  white:    '#ffffff',
  text:     '#1a2e1b',
  textMed:  '#4a6a4c',
  textLight:'#8aaa8c',
  border:   '#e0ece0',
  shadow:   'rgba(30,80,32,0.10)',
};

const CATEGORY_PALETTE = [
  '#c0392b','#2a7a2d','#c8761a','#1a6fa8','#7c3aed','#0e7490','#b45309','#047857',
];
function catColor(title) {
  const h = title.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return CATEGORY_PALETTE[h % CATEGORY_PALETTE.length];
}

const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS  = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const PRIORITY_STYLE = {
  high:   { bg: '#fff8e6', color: '#c8761a', label: 'High' },
  medium: { bg: T.green50, color: T.green700, label: 'Medium' },
  low:    { bg: '#f5f5f5', color: '#8aaa8c', label: 'Low' },
};

function PriorityPill({ priority }) {
  const s = PRIORITY_STYLE[priority] || PRIORITY_STYLE.medium;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
      padding: '2px 6px', borderRadius: 99,
      background: s.bg, color: s.color,
      textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {s.label}
    </span>
  );
}

function Checkbox({ checked, onChange }) {
  return (
    <div onClick={onChange} style={{
      width: 18, height: 18, borderRadius: 5,
      border: checked ? 'none' : `2px solid ${T.border}`,
      background: checked ? T.green600 : T.white,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
    }}>
      {checked && (
        <svg width="10" height="8" viewBox="0 0 12 10">
          <polyline points="1,5 4,9 11,1" stroke="white" strokeWidth="2"
            fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

// ── CalendarView ───────────────────────────────────────────────────────────────
const CalendarView = ({
  monthName,
  activeYear,
  monthData,
  completedTasks,
  onTaskToggle,
  onMonthChange,
  monthStats,
  isDesktop = false,
}) => {
  const [selectedWeekIdx, setSelectedWeekIdx] = useState(null);

  const monthIndex = MONTHS_FULL.indexOf(monthName);

  // Build calendar grid
  const { weeks } = useMemo(() => {
    const firstDow   = new Date(activeYear, monthIndex, 1).getDay();
    const daysInMonth = new Date(activeYear, monthIndex + 1, 0).getDate();
    const cells = [
      ...Array(firstDow).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
    while (cells.length % 7 !== 0) cells.push(null);
    const ws = [];
    for (let i = 0; i < cells.length; i += 7) ws.push(cells.slice(i, i + 7));
    return { weeks: ws };
  }, [activeYear, monthIndex]);

  // Assign sections to weeks evenly
  const sections = useMemo(() => monthData?.sections || [], [monthData]);
  const sectionsByWeek = useMemo(() => {
    const map = {};
    sections.forEach((section, sIdx) => {
      const wk = weeks.length <= 1 ? 0 : Math.min(
        Math.floor(sIdx * weeks.length / sections.length),
        weeks.length - 1
      );
      if (!map[wk]) map[wk] = [];
      map[wk].push({ section, sIdx, color: catColor(section.title) });
    });
    return map;
  }, [sections, weeks.length]);

  // Per-day section assignment (for dot display)
  const daySection = useMemo(() => {
    const map = {};
    sections.forEach((section, sIdx) => {
      const daysInMonth = new Date(activeYear, monthIndex + 1, 0).getDate();
      const startDay = Math.floor(sIdx * daysInMonth / sections.length) + 1;
      const endDay   = Math.floor((sIdx + 1) * daysInMonth / sections.length);
      for (let d = startDay; d <= endDay; d++) {
        if (!map[d]) map[d] = [];
        map[d].push({ sIdx, color: catColor(section.title) });
      }
    });
    return map;
  }, [sections, activeYear, monthIndex]);

  const isSectionComplete = (sIdx) =>
    sections[sIdx]?.tasks.every((_, tIdx) =>
      completedTasks.has(`${activeYear}-${monthName}-${sIdx}-${tIdx}`)
    ) ?? false;

  const today = new Date();
  const isToday = (day) =>
    day !== null &&
    today.getFullYear() === activeYear &&
    today.getMonth() === monthIndex &&
    today.getDate() === day;

  const handleMonthNav = (dir) => {
    const newIdx = ((monthIndex + dir) + 12) % 12;
    const newMonth = MONTHS_FULL[newIdx];
    let newYear = activeYear;
    if (dir === 1 && monthIndex === 11) newYear = activeYear + 1;
    if (dir === -1 && monthIndex === 0) newYear = activeYear - 1;
    if (onMonthChange) onMonthChange(newMonth, newYear);
    setSelectedWeekIdx(null);
  };

  const selectedWeekSections = selectedWeekIdx !== null
    ? (sectionsByWeek[selectedWeekIdx] || [])
    : [];

  // All sections if nothing selected
  const displaySections = selectedWeekIdx !== null ? selectedWeekSections : [];

  const season = monthData?.season
    ? monthData.season.charAt(0).toUpperCase() + monthData.season.slice(1)
    : '';
  const seasonEmoji = { spring: '🌷', summer: '☀️', fall: '🍂', winter: '❄️' }[monthData?.season] || '🌿';

  return (
    <div style={{
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      background: T.bg,
      ...(isDesktop ? {} : { minHeight: '100%', padding: '0 0 16px' }),
    }}>

      {/* ── Month navigator ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isDesktop ? '0 0 20px' : '16px 16px 12px',
      }}>
        <button
          onClick={() => handleMonthNav(-1)}
          style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: 8, width: 34, height: 34,
            cursor: 'pointer', fontSize: 18, color: T.textMed,
          }}
        >‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: isDesktop ? 20 : 17, fontWeight: 700, color: T.text }}>
            {monthName} {activeYear}
          </div>
          {season && (
            <div style={{ fontSize: 11, color: T.green700, fontWeight: 600, marginTop: 1 }}>
              {seasonEmoji} {season} · {monthStats?.completed || 0}/{monthStats?.total || 0} tasks done
            </div>
          )}
        </div>
        <button
          onClick={() => handleMonthNav(1)}
          style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: 8, width: 34, height: 34,
            cursor: 'pointer', fontSize: 18, color: T.textMed,
          }}
        >›</button>
      </div>

      {/* ── Calendar card ────────────────────────────────────────────────── */}
      <div style={{
        background: T.white,
        borderRadius: isDesktop ? 16 : 20,
        boxShadow: `0 2px 12px ${T.shadow}`,
        overflow: 'hidden',
        margin: isDesktop ? '0 0 20px' : '0 16px 14px',
      }}>
        {/* Day-of-week header */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
          background: T.green50, borderBottom: `1px solid ${T.border}`,
        }}>
          {DAY_LABELS.map(d => (
            <div key={d} style={{
              textAlign: 'center', padding: '8px 0',
              fontSize: 11, fontWeight: 700,
              color: d === 'Su' || d === 'Sa' ? T.textLight : T.textMed,
            }}>{d}</div>
          ))}
        </div>

        {/* Week rows */}
        {weeks.map((week, wIdx) => {
          const weekSections = sectionsByWeek[wIdx] || [];
          const isSelected   = selectedWeekIdx === wIdx;
          return (
            <div
              key={wIdx}
              onClick={() => setSelectedWeekIdx(isSelected ? null : wIdx)}
              style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                background: isSelected ? T.green50 : T.white,
                borderBottom: wIdx < weeks.length - 1 ? `1px solid ${T.border}` : 'none',
                cursor: 'pointer', transition: 'background 0.15s',
                position: 'relative',
              }}
            >
              {week.map((day, dIdx) => (
                <div
                  key={dIdx}
                  style={{
                    padding: isDesktop ? '10px 6px' : '8px 4px',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 3, minHeight: isDesktop ? 56 : 46,
                  }}
                >
                  {/* Day number */}
                  <div style={{
                    width: isDesktop ? 28 : 24,
                    height: isDesktop ? 28 : 24,
                    borderRadius: '50%',
                    background: isToday(day) ? T.green700 : 'transparent',
                    color: isToday(day) ? T.white : day ? T.text : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: isDesktop ? 13 : 12, fontWeight: isToday(day) ? 700 : 400,
                  }}>
                    {day || ''}
                  </div>

                  {/* Section dots for this day */}
                  {day && (daySection[day] || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {(daySection[day] || []).map(({ sIdx, color }) => (
                        <div
                          key={sIdx}
                          title={sections[sIdx]?.title || ''}
                          style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: isSectionComplete(sIdx) ? T.green600 : color,
                            opacity: isSectionComplete(sIdx) ? 1 : 0.7,
                            position: 'relative',
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Week section labels on the right */}
              {weekSections.length > 0 && (
                <div style={{
                  position: 'absolute', right: 4, top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex', gap: 4,
                }}>
                  {weekSections.map(({ sIdx, color }) => (
                    <div
                      key={sIdx}
                      title={sections[sIdx]?.title || ''}
                      style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: isSectionComplete(sIdx) ? T.green600 : color,
                        border: isSectionComplete(sIdx) ? 'none' : `1.5px solid ${color}`,
                        position: 'relative',
                      }}
                    >
                      {isSectionComplete(sIdx) && (
                        <svg
                          width="6" height="5" viewBox="0 0 8 7"
                          style={{ position: 'absolute', top: 1, left: 1 }}
                        >
                          <polyline points="1,3 3,6 7,1" stroke="white"
                            strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                        </svg>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Section legend ────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
        margin: isDesktop ? '0 0 20px' : '0 16px 14px',
      }}>
        {sections.map((section, sIdx) => {
          const color    = catColor(section.title);
          const complete = isSectionComplete(sIdx);
          const done     = section.tasks.filter((_, tIdx) =>
            completedTasks.has(`${activeYear}-${monthName}-${sIdx}-${tIdx}`)
          ).length;
          return (
            <div
              key={sIdx}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: T.white, borderRadius: 99,
                padding: '5px 10px',
                border: `1px solid ${complete ? T.green200 : T.border}`,
                boxShadow: `0 1px 3px ${T.shadow}`,
              }}
            >
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0,
              }} />
              <span style={{
                fontSize: 11, fontWeight: 600,
                color: complete ? T.green700 : T.textMed,
              }}>
                {section.title}
              </span>
              <span style={{ fontSize: 10, color: T.textLight }}>
                {done}/{section.tasks.length}
              </span>
              {complete && (
                <span style={{ fontSize: 10, color: T.green700 }}>✓</span>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Task detail panel ────────────────────────────────────────────── */}
      {selectedWeekIdx !== null && (
        <div style={{ margin: isDesktop ? '0 0 20px' : '0 16px 14px' }}>
          {displaySections.length === 0 ? (
            <div style={{
              background: T.white, borderRadius: 14, padding: '16px 18px',
              fontSize: 13, color: T.textLight, textAlign: 'center',
              boxShadow: `0 1px 4px ${T.shadow}`,
            }}>
              No tasks assigned to this week
            </div>
          ) : (
            displaySections.map(({ section, sIdx, color }) => (
              <div key={sIdx} style={{ marginBottom: 12 }}>
                {/* Section header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: 3,
                    background: color, flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                    {section.icon} {section.title}
                  </span>
                </div>
                {/* Task list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {section.tasks.map((task, tIdx) => {
                    const taskId = `${activeYear}-${monthName}-${sIdx}-${tIdx}`;
                    const done   = completedTasks.has(taskId);
                    return (
                      <div
                        key={tIdx}
                        style={{
                          background: T.white, borderRadius: 12,
                          padding: '11px 14px',
                          display: 'flex', alignItems: 'center', gap: 10,
                          boxShadow: `0 1px 4px ${T.shadow}`,
                          opacity: done ? 0.55 : 1,
                          borderLeft: `3px solid ${color}`,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        <Checkbox checked={done} onChange={() => onTaskToggle && onTaskToggle(taskId)} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 12, fontWeight: 600, color: T.text, lineHeight: 1.3,
                            textDecoration: done ? 'line-through' : 'none',
                          }}>
                            {task.text}
                          </div>
                        </div>
                        <PriorityPill priority={task.priority || 'medium'} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Prompt when nothing selected */}
      {selectedWeekIdx === null && (
        <div style={{
          textAlign: 'center', padding: '12px 16px',
          fontSize: 12, color: T.textLight,
        }}>
          Tap any week to see its tasks
        </div>
      )}
    </div>
  );
};

export default CalendarView;
